import { ApplyModule, ApplyForProgramsOrScholarshipsRequestPayload } from '../../store/features/apply/types';
import {
  ApplyItem,
  ScholarshipApplyItemWithProgram,
  ProgramApplyItem,
  ScholarshipApplyItem,
  SelectedApplyItem,
} from './types';
import { StrOrNum } from '../../types/util';
import { EligibilityItem } from '../../components/eligibility-fail-content/types';
import isPast from 'date-fns/isPast';

export const isProgramApplyItem = (applyItem: ApplyItem): applyItem is ProgramApplyItem => 'name' in applyItem;

// Get scholarship that needs user to select program in case of single apply
export const getScholarshipItemWithPrograms = (
  applyItems: ApplyItem | ApplyItem[],
  module: ApplyModule
): ScholarshipApplyItemWithProgram | null => {
  if (
    module === 'scholarships' &&
    !Array.isArray(applyItems) &&
    'programs' in applyItems &&
    applyItems.programs &&
    applyItems.programs.length > 0
  ) {
    const programs = applyItems.programs;
    return {
      ...applyItems,
      programs,
    };
  }
  return null;
};

// Convert Apply items to Request Data for Apply
export const convertApplyItemsToApplyPayload = (
  applyItems: ApplyItem | ApplyItem[],
  module: ApplyModule,
  userSlug: string,
  userId: StrOrNum
): ApplyForProgramsOrScholarshipsRequestPayload | undefined => {
  if (Array.isArray(applyItems) && applyItems.length > 0) {
    return {
      type: module,
      slug: userSlug,
      user_id: userId.toString(),
      items: applyItems.map((item) => ({
        apply: item.id,
        has_program: 'programs' in item ? item.programs?.length !== 0 : false,
        secondary_apply: 'selectedProgramId' in item ? item.selectedProgramId : undefined,
      })),
    };
  } else if (module === 'institute_programs') {
    const item = applyItems as ProgramApplyItem;
    return {
      type: module,
      items: [{ apply: item.id }],
      slug: userSlug,
      user_id: userId.toString(),
    };
  } else if (module === 'scholarships') {
    const item = applyItems as ScholarshipApplyItem;
    if (!item.programs || item.programs.length === 0) {
      return {
        type: module,
        items: [{ apply: item.id, has_program: false }],
        slug: userSlug,
        user_id: userId.toString(),
      };
    } else {
      if (item.selectedProgramId) {
        return {
          type: module,
          items: [{ apply: item.id, has_program: true, secondary_apply: item.selectedProgramId }],
          user_id: userId.toString(),
          slug: userSlug,
        };
      }
    }
  }
};

export const getParsedEligibilityItems = (applyItems: ApplyItem | ApplyItem[]): EligibilityItem | EligibilityItem[] => {
  if (Array.isArray(applyItems)) {
    return applyItems.reduce((prevEligibilityItems, item) => {
      if ('name' in item) {
        prevEligibilityItems.push({
          id: item.id,
          name: item.name,
          type: 'institute_programs',
        });
      } else {
        prevEligibilityItems.push({
          id: item.id,
          name: item.title,
          type: 'scholarships',
        });
        if (item.selectedProgramId && item.programs && item.programs.length > 0) {
          const selectedProgram = item.programs.find(
            (program) => program.id.toString() === item.selectedProgramId?.toString()
          );
          if (selectedProgram) {
            const isSelectedProgramAlreadyAdded =
              prevEligibilityItems.findIndex((item) => item.id === `program-${selectedProgram.id}`) !== -1;
            if (!isSelectedProgramAlreadyAdded) {
              prevEligibilityItems.push({
                id: selectedProgram.id,
                name: selectedProgram.name,
                type: 'institute_programs',
              });
            }
          }
        }
      }
      return prevEligibilityItems;
    }, [] as EligibilityItem[]);
  } else {
    if ('name' in applyItems) {
      return {
        id: applyItems.id,
        name: applyItems.name,
        type: 'institute_programs',
      };
    } else {
      const eligibilityItem: EligibilityItem = {
        id: applyItems.id,
        name: applyItems.title,
        type: 'scholarships',
      };
      if (applyItems.selectedProgramId && applyItems.programs && applyItems.programs.length > 0) {
        const selectedProgramId = applyItems.selectedProgramId.toString();
        const selectedProgram = applyItems.programs.find((program) => program.id.toString() === selectedProgramId);
        if (selectedProgram) {
          const programEligibilityItem: EligibilityItem = {
            id: selectedProgram.id,
            name: selectedProgram.name,
            type: 'institute_programs',
          };
          return [eligibilityItem, programEligibilityItem];
        }
      }
      return eligibilityItem;
    }
  }
};

export const getApplicableApplyItems = (applyItems: (ApplyItem | SelectedApplyItem)[]): typeof applyItems => {
  return applyItems.reduce((prevItems, applyItem) => {
    const isApplicable = applyItem.is_applicable && applyItem.is_published && !isPast(new Date(applyItem.deadline));
    if (isApplicable) {
      if ('name' in applyItem) {
        if (!applyItem.providers || applyItem.providers.is_published) {
          prevItems.push(applyItem);
        }
      } else {
        if (applyItem.programs && applyItem.programs.length > 0) {
          const programs = applyItem.programs?.filter(
            (program) => program.is_applicable && program.is_published && program.providers.is_published
          );
          if (programs.length > 0) {
            prevItems.push({
              ...applyItem,
              programs,
            });
          }
        } else {
          prevItems.push(applyItem);
        }
      }
    }
    return prevItems;
  }, [] as ApplyItem[]);
};

export const convertApplyItemsToMassApplyItems = (applyItems: ApplyItem | ApplyItem[]): SelectedApplyItem[] => {
  if (Array.isArray(applyItems)) {
    return getApplicableApplyItems(applyItems).map((item) => {
      if (isProgramApplyItem(item)) {
        return { ...item, isSelected: false };
      } else {
        return { ...item, isSelected: false, selectedProgramId: item.programs && item.programs[0]?.id.toString() };
      }
    });
  } else {
    return [];
  }
};
